import * as React from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import { useAppTranslation } from '../../../services/i18n';
import { MUTED_TEXT, ROW_SEPARATOR } from '../colors';

export type StructuredField = {
  /** Key on the item object this input is bound to. */
  key: string;
  label?: string;
  placeholder?: string;
  width?: string;
};

type Props<T extends Record<string, unknown>> = {
  /** Omitted when the field sits inside a `DerivedField`, which labels it. */
  label?: string;
  hint?: string;
  items: T[];
  fields: StructuredField[];
  /** Applied to a row the user adds, so a new item is not half-formed. */
  newItem: () => T;
  onChange: (items: T[]) => void;
  readOnly?: boolean;
  /** Shown instead of an empty list, before Klara has filled anything in. */
  emptyHint?: string;
};

/**
 * Editor for an array of objects where only some keys are shown.
 *
 * This exists to protect the round trip. The partner requires
 * final_constants / final_variables back with every field intact, and several
 * arrays carry keys the design never renders — required_content.depth, which
 * decides how deeply each topic is generated, terminology.original and .usage,
 * question_areas.questions.
 *
 * The items stay objects in React state and each input is bound to one key, so
 * the unrendered keys survive simply by never being touched. Rendering these as
 * one textarea and re-parsing lines would mean re-deriving the objects on save,
 * and a reordered or retyped line would silently attach the wrong `depth` to
 * the wrong topic — visible only in the finished course.
 */
export function StructuredList<T extends Record<string, unknown>>({
  label,
  hint,
  items,
  fields,
  newItem,
  onChange,
  readOnly,
  emptyHint,
}: Props<T>) {
  const t = useAppTranslation();

  const setField = (index: number, key: string, value: string) => {
    const next = items.map((item, i) =>
      i === index ? { ...item, [key]: value } : item
    );
    onChange(next);
  };

  const removeAt = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ marginBottom: label ? '24px' : 0 }}>
      {label && (
        <Typography sx={{ fontWeight: 600, marginBottom: '8px' }}>
          {label}
        </Typography>
      )}

      {items.length === 0 && emptyHint && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            padding: '12px',
            border: `1px dashed ${ROW_SEPARATOR}`,
            borderRadius: '4px',
            color: MUTED_TEXT,
            marginBottom: '8px',
          }}
        >
          {emptyHint}
        </Typography>
      )}

      {items.map((item, index) => (
        <Box
          key={index}
          sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '8px' }}
        >
          {fields.map((field) => (
            <TextField
              variant="standard"
              key={field.key}
              size="small"
              fullWidth
              multiline
              label={field.label}
              placeholder={field.placeholder}
              value={(item[field.key] as string) ?? ''}
              disabled={readOnly}
              onChange={(e) => setField(index, field.key, e.target.value)}
              sx={{ width: field.width ?? '100%' }}
            />
          ))}
          {!readOnly && (
            <IconButton
              aria-label={t('klara.brief.removeItem')}
              onClick={() => removeAt(index)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ))}

      {!readOnly && (
        <IconButton
          aria-label={t('klara.brief.addItem')}
          onClick={() => onChange([...items, newItem()])}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      )}

      {hint && (
        <Typography variant="caption" sx={{ color: MUTED_TEXT }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The plain-string counterpart: one item per line is safe here precisely
 * because there is nothing hidden to lose.
 */
export function StringListField({
  label,
  hint,
  value,
  onChange,
  readOnly,
  placeholder,
  rows = 4,
  emptyHint,
}: {
  /** Omitted when the field sits inside a `DerivedField`, which labels it. */
  label?: string;
  hint?: string;
  value: string[] | undefined;
  onChange: (value: string[]) => void;
  readOnly?: boolean;
  placeholder?: string;
  rows?: number;
  /** Replaces an empty read-only box, before Klara has filled anything in. */
  emptyHint?: string;
}) {
  // The raw text is kept locally: deriving it from `value` would drop the blank
  // line Enter just made, so a new item could never be started.
  const [text, setText] = React.useState(() => (value ?? []).join('\n'));

  // Adopt an outside change (Klara's draft arriving, a reset), but not the echo
  // of our own edit, which differs from the text only by blank lines.
  React.useEffect(() => {
    setText((current) =>
      JSON.stringify(toLines(current)) === JSON.stringify(value ?? [])
        ? current
        : (value ?? []).join('\n')
    );
  }, [value]);

  // An editable empty field keeps its box, otherwise there is nowhere to type.
  if (readOnly && emptyHint && !(value ?? []).length) {
    return (
      <Box sx={{ marginBottom: label ? '24px' : 0 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            padding: '12px',
            border: `1px dashed ${ROW_SEPARATOR}`,
            borderRadius: '4px',
            color: MUTED_TEXT,
          }}
        >
          {emptyHint}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ marginBottom: label ? '24px' : 0 }}>
      <TextField
        variant="standard"
        label={label}
        fullWidth
        multiline
        minRows={rows}
        disabled={readOnly}
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(toLines(e.target.value));
        }}
      />
      {hint && (
        <Typography variant="caption" sx={{ color: MUTED_TEXT }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

function toLines(text: string) {
  return text.split('\n').filter((line) => line.trim() !== '');
}
